import React from 'react';
import useAccessibleBg from '../../../../../hooks/useAccessibleBg';
import useProgramHeroPalette from '../useProgramHeroPalette';
import { getResponsiveImageSources } from '../../../../shared/OptimizedImage/optimizedImage';
import {
  formatScheduleDaysSummary,
  getScheduleTimingSummaries,
} from './ProgramSchedule';

type Props = {
  content?: any;
  imageUrl?: string;
};

function resolvePresenters(content: any): string {
  const presenters = content?.presenters;

  if (typeof presenters === 'string' && presenters.trim()) {
    return presenters.trim();
  }

  return '';
}

export default function ProgramHero({ content, imageUrl }: Props) {
  const highlightTimeout = React.useRef<number>();
  const title = content?.title || '';
  const presenters = resolvePresenters(content);
  const scheduleSummaries = getScheduleTimingSummaries(
    content?.schedule?.items || [],
  );
  const visibleScheduleSummaries = scheduleSummaries.slice(0, 2);

  const heroBg = useAccessibleBg(imageUrl, '#C95F42');
  const palette = useProgramHeroPalette(imageUrl);
  const imageSources = getResponsiveImageSources(imageUrl);

  React.useEffect(
    () => () => {
      if (highlightTimeout.current) {
        window.clearTimeout(highlightTimeout.current);
      }
    },
    [],
  );

  const focusFullSchedule = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const schedule = document.getElementById('quando-vai-ao-ar');
    if (!schedule) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    schedule.focus({ preventScroll: true });
    schedule.classList.remove('is-highlighted');
    void schedule.offsetWidth;
    schedule.classList.add('is-highlighted');

    if (highlightTimeout.current) {
      window.clearTimeout(highlightTimeout.current);
    }
    highlightTimeout.current = window.setTimeout(() => {
      schedule.classList.remove('is-highlighted');
    }, 1800);

    schedule.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
    window.history.replaceState(null, '', '#quando-vai-ao-ar');
  };

  return (
    <section
      className="program-view__hero"
      style={
        {
          background: heroBg,
          '--program-hero-subtitle': palette.subtitle,
          '--program-hero-image-border': palette.imageBorder,
          '--program-hero-bottom-border': palette.bottomBorder,
        } as React.CSSProperties
      }
    >
      {imageSources ? (
        <div className="program-view__heroImage">
          <img
            src={imageSources.src}
            srcSet={imageSources.fallbackSrcSet}
            sizes="(max-width: 767px) 220px, 320px"
            width={320}
            height={320}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            alt={title}
          />
        </div>
      ) : null}

      <div className="program-view__heroContent">
        <h1 className="program-view__title">{title}</h1>

        {presenters ? (
          <div className="program-view__subtitle-wrapper">
            <span>Apresentação: </span>
            <p className="program-view__subtitle">{presenters}</p>
          </div>
        ) : null}

        {visibleScheduleSummaries.length ? (
          <div
            className="program-view__heroSchedule"
            aria-label="Resumo de quando o programa vai ao ar"
          >
            <span className="program-view__heroScheduleLabel">No ar</span>
            <div className="program-view__heroScheduleLines">
              {visibleScheduleSummaries.map((summary) => (
                <span
                  key={`${summary.days.join('-')}-${summary.times.join('-')}`}
                  className="program-view__heroScheduleLine"
                >
                  <span>{formatScheduleDaysSummary(summary.days)}</span>
                  <span className="program-view__heroScheduleDivider"> · </span>
                  <span>{summary.times.join(', ')}</span>
                </span>
              ))}
              {scheduleSummaries.length > visibleScheduleSummaries.length ? (
                <a
                  className="program-view__heroScheduleMore"
                  href="#quando-vai-ao-ar"
                  onClick={focusFullSchedule}
                >
                  Ver programação completa
                  <span aria-hidden="true">↓</span>
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
